import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryKey,
} from "@tanstack/react-query"
import { toast } from "react-toastify"
import { crudFactory } from "../genericCrudFactory"

export const useListQuery = <T>(url: string, params?: Record<string, any>) => {
  const client = crudFactory<T>(url)
  return useQuery({
    queryKey: [url, params] as QueryKey,
    queryFn: () => client.findAll(params),
    select: response => (Array.isArray(response) ? response : response.data),
  })
}

export const useDetailQuery = <T>(url: string, id?: string | number) => {
  const client = crudFactory<T>(url)
  return useQuery({
    queryKey: [url, id] as QueryKey,
    queryFn: () => client.findOne(id as string | number),
    enabled: !!id,
  })
}

export const useCreateMutation = <T, CreateDto = Partial<T>>(url: string) => {
  const client = crudFactory<T, CreateDto>(url)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateDto) => client.create(data),
    onSuccess: () => {
      toast.success(`${url} created successfully`)
      queryClient.invalidateQueries({ queryKey: [url] })
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ??
          "Something went wrong. Contact the admin."
      )
    },
  })
}

export const useUpdateMutation = <T, UpdateDto = Partial<T>>(url: string) => {
  const client = crudFactory<T, any, UpdateDto>(url)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: UpdateDto }) =>
      client.update(id, data),
    onSuccess: () => {
      toast.success(`${url} updated successfully`)
      queryClient.invalidateQueries({ queryKey: [url] })
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? "Something went wrong.")
    },
  })
}

export const useDeleteMutation = <T>(url: string) => {
  const client = crudFactory<T>(url)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string | number) => client.remove(id),
    onSuccess: () => {
      toast.success(`${url} deleted successfully`)
      queryClient.invalidateQueries({ queryKey: [url] })
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? "Something went wrong.")
    },
  })
}
